import { I } from '@/shared/icons';
import type { PropertyOption } from './PropertyCombobox';

interface Props {
  options: PropertyOption[];
  isChecked: (id: string) => boolean;
  search: string;
  onSearch: (next: string) => void;
  onToggle: (id: string) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
}

/** Die geöffnete Auswahlfläche der Apartment-Combobox: Suche, Kurzwege und Liste. */
export function PropertyComboboxPanel({
  options,
  isChecked,
  search,
  onSearch,
  onToggle,
  onSelectAll,
  onSelectNone,
}: Props) {
  return (
    <div
      id="apartment-auswahl"
      className="border-line bg-card absolute left-0 top-full z-30 mt-1 w-72 max-w-[calc(100vw-2rem)] rounded-md border shadow-md"
    >
      <div className="border-line/40 border-b p-2">
        <div className="relative">
          <I
            name="Search"
            size={12}
            className="text-ink-faint pointer-events-none absolute left-2 top-1/2 -translate-y-1/2"
            aria-hidden
          />
          <input
            autoFocus
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Apartment suchen…"
            aria-label="Apartment suchen"
            className="border-line bg-paper text-ink placeholder:text-ink-faint focus:border-forest h-9 w-full rounded border pl-7 pr-2 text-xs focus:outline-none"
          />
        </div>
        <div className="mt-2 flex gap-1.5">
          <ActionButton onClick={onSelectAll}>Alle</ActionButton>
          <ActionButton onClick={onSelectNone}>Keins</ActionButton>
        </div>
      </div>
      {/*
        Bewusst keine `listbox`-Rolle: ARIA verlangt dafür `option`-Kinder mit `aria-selected`
        und eigener Fokusführung. Eine Liste beschrifteter Kästchen ist ein vollwertiges Muster
        und für die Sprachausgabe hier das ehrlichere — eine Auswahlliste mit null Einträgen
        wäre sie sonst.
      */}
      <ul className="max-h-72 overflow-y-auto py-1">
        {options.length === 0 ? (
          <li className="text-ink-soft px-3 py-4 text-center text-xs">Kein Treffer</li>
        ) : (
          options.map((o) => (
            <li key={o.id}>
              {/* 44 px nach §9 — die Zeilen maßen rund 26 px und sind der einzige Weg zur Auswahl. */}
              <label className="hover:bg-card-alt flex min-h-11 cursor-pointer items-center gap-2 px-3 text-xs">
                <input
                  type="checkbox"
                  checked={isChecked(o.id)}
                  onChange={() => onToggle(o.id)}
                  className="accent-forest size-4 cursor-pointer"
                />
                <span className="text-ink truncate">{o.name}</span>
              </label>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

function ActionButton({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="border-line/60 text-ink-soft hover:bg-card-alt hover:text-ink min-h-11 flex-1 rounded border px-2 text-[11px] transition-colors"
    >
      {children}
    </button>
  );
}
